import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, MessagesSquare, Globe, CreditCard, Ticket, LogOut, Shield, Hourglass, Archive, Image as ImageIcon, Unlink, Copy, Check, History, Users, Brush, ChevronDown, Scale, MessageSquareQuote, Rocket } from 'lucide-react';
import type { User } from 'firebase/auth';
import type { SubscriptionInfo } from '../hooks/useSubscription';
import type { CodeRecord } from '../api/codes';
import EditLog from './EditLog';
import PendingPromotions from './PendingPromotions';
import UserManager from './UserManager';
import SportRulesModal from './SportRulesModal';
import ResponseGuideModal from './ResponseGuideModal';
import { DISCORD_INVITE } from '../data/topics';

interface SettingsModalProps {
  user: User | null;
  sub: SubscriptionInfo;
  inline?: boolean;
  onClose: () => void;
  onLogOut: () => void;
  /** Admin: jump to the library showing only pending or archived media. */
  onAdminView?: (mode: 'pending' | 'archived') => void;
  /** Queue sizes — each row is hidden when its count is zero. */
  pendingCount?: number;
  archivedCount?: number;
}

const ADMIN_UID = 'bZj8y9DApEac3eKOuKV08MKcaDx2';

export default function SettingsModal({ user, sub, inline, onClose, onLogOut, onAdminView, pendingCount = 0, archivedCount = 0 }: SettingsModalProps) {
  const isAdmin = user?.uid === ADMIN_UID;
  // Janitors: trusted members who can edit/archive techniques. Their changes
  // are logged; the log is where they (and the admin) review and revert.
  const isJanitor = !isAdmin && sub.janitor;
  const [editLogOpen, setEditLogOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [promosOpen, setPromosOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  // Canned replies for questions about where the video comes from (admin only).
  const [guideOpen, setGuideOpen] = useState(false);
  // Admin Actions start collapsed so the everyday settings aren't buried under them.
  const [adminOpen, setAdminOpen] = useState(false);

  // Grid-view thumbnails for techniques that predate them (admin only). Runs in
  // throttled batches to avoid a burst of scrapes against Instagram/TikTok, so
  // it's meant to be pressed repeatedly until `remaining` reaches zero.
  const [thumbBusy, setThumbBusy] = useState(false);
  const [thumbResult, setThumbResult] = useState<string | null>(null);
  const runBackfillThumbnails = async () => {
    if (thumbBusy) return;
    setThumbBusy(true);
    setThumbResult(null);
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../firebase');
      // A batch is ~25 × (scrape + image fetch + upload + 1.5s throttle), which
      // blows past httpsCallable's 70s default and surfaces as
      // deadline-exceeded even though the function runs on to completion.
      // Matches the function's own timeoutSeconds: 540.
      const res = await httpsCallable(functions, 'backfillThumbnails', { timeout: 540_000 })({ limit: 25 });
      const { done, failed, remaining, reasons } = res.data as {
        done: number; failed: number; remaining: number; reasons?: Record<string, number>;
      };
      // Name the biggest failure reason: "12 failed" says nothing about whether
      // to retry, re-scrape or archive the clips.
      const top = Object.entries(reasons ?? {}).sort((a, b) => b[1] - a[1])[0];
      const failNote = failed ? `, ${failed} failed${top ? ` (${top[0]})` : ''}` : '';
      setThumbResult(`+${done}${failNote} · ${remaining} left`);
    } catch (e) {
      setThumbResult(e instanceof Error ? e.message : 'failed');
    } finally {
      setThumbBusy(false);
    }
  };

  // Dead-link sweep (admin only). Normally runs nightly on its own; this is for
  // when you want a pass now. Archives only after three consecutive daily
  // failures, and aborts without recording anything if most of a batch fails.
  const [sweepBusy, setSweepBusy] = useState(false);
  const [sweepResult, setSweepResult] = useState<string | null>(null);
  const runSweepDeadLinks = async () => {
    if (sweepBusy) return;
    setSweepBusy(true);
    setSweepResult(null);
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../firebase');
      const res = await httpsCallable(functions, 'sweepDeadLinks', { timeout: 540_000 })({ limit: 25 });
      const { checked, archived, aborted, failureRate, remaining } = res.data as
        { checked: number; archived: number; aborted: boolean; failureRate: number; remaining: number };
      setSweepResult(aborted
        ? `aborted: ${Math.round(failureRate * 100)}% failed`
        : `${checked} checked, ${archived} archived · ${remaining} left`);
    } catch (e) {
      setSweepResult(e instanceof Error ? e.message : 'failed');
    } finally {
      setSweepBusy(false);
    }
  };

  // Gift-code generator modal (admin only).
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftMonths, setGiftMonths] = useState('12');
  const [giftCode, setGiftCode] = useState<string | null>(null);
  const [giftBusy, setGiftBusy] = useState(false);
  const [giftErr, setGiftErr] = useState<string | null>(null);

  // Every code ever minted, so a generated one is never lost and you can see
  // which gifts were actually redeemed.
  const [codes, setCodes] = useState<CodeRecord[] | null>(null);
  const [codesErr, setCodesErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const loadCodes = async () => {
    setCodesErr(null);
    try {
      const { listCodes } = await import('../api/codes');
      setCodes(await listCodes());
    } catch (e) {
      setCodesErr(e instanceof Error ? e.message : 'Could not load codes');
    }
  };

  const openGiftModal = () => {
    setGiftCode(null);
    setGiftErr(null);
    setGiftMonths('12');
    setCodes(null);
    setGiftOpen(true);
    loadCodes();
  };

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied((c) => (c === code ? null : c)), 1500);
    } catch { /* clipboard may be blocked */ }
  };

  const handleGenerateCode = async () => {
    const months = Number(giftMonths) || 12;
    setGiftBusy(true);
    setGiftErr(null);
    try {
      const { generateCodes } = await import('../api/codes');
      // The backend takes days; 12 months maps to the prior 365-day default.
      const days = Math.round((months * 365) / 12);
      const [code] = await generateCodes(1, days);
      setGiftCode(code);
      try { await navigator.clipboard.writeText(code); } catch { /* clipboard may be blocked */ }
      loadCodes();
    } catch (e) {
      setGiftErr(e instanceof Error ? e.message : 'Failed to generate code');
    } finally {
      setGiftBusy(false);
    }
  };

  const content = (
    <div
      className={`settings-modal${inline ? ' settings-modal--page' : ''}`}
      onClick={(e) => e.stopPropagation()}
      style={inline ? { position: 'static', width: '100%', minHeight: '100%' } : undefined}
    >
      <div className="settings-modal-header">
        <div className="settings-modal-header-top">
          <span className="settings-modal-status">
            {isAdmin ? 'Gnosis Admin' : isJanitor ? 'Gnosis Janitor' : `Gnosis ${sub.status === 'active' ? 'Pro' : ''}`}
            {sub.paidVia === 'stripe' && sub.inTrial && <span className="plan-chip plan-chip--trial">TRIAL</span>}
            {sub.paidVia === 'stripe' && !sub.inTrial && sub.plan === 'yearly' && <span className="plan-chip plan-chip--pro">YEARLY</span>}
            {sub.paidVia === 'stripe' && !sub.inTrial && sub.plan === 'monthly' && <span className="plan-chip plan-chip--pro">MONTHLY</span>}
            {sub.paidVia === 'code' && <span className="plan-chip plan-chip--code">GIFT</span>}
          </span>
          {!inline && (
            <button className="settings-modal-header-close" onClick={onClose}>
              <X size={18} />
            </button>
          )}
        </div>
        <div className="settings-modal-header-bottom">
          <span className="settings-modal-name">{user?.displayName || 'Settings'}</span>
          {user?.email && <span className="settings-modal-email">{user.email}</span>}
        </div>
      </div>
      <div className="settings-modal-actions">
        {isAdmin && (
          <div className="settings-admin-panel">
            <button
              type="button"
              className="settings-admin-panel-title settings-admin-panel-toggle"
              onClick={() => setAdminOpen(o => !o)}
              aria-expanded={adminOpen}
            >
              <Shield size={14} />
              <span>Admin Actions</span>
              <ChevronDown size={14} className={`settings-admin-panel-chevron${adminOpen ? ' open' : ''}`} />
            </button>
            {adminOpen && (<>
            <button className="settings-modal-btn" onClick={openGiftModal}>
              <Ticket size={16} />
              Generate Invite Code
            </button>
            {/* Only worth a row when there's something in the queue. */}
            {pendingCount > 0 && (
              <button type="button" className="settings-modal-btn" onClick={() => onAdminView?.('pending')}>
                <Hourglass size={16} />
                View Pending Media
                <span className="settings-alias-current">{pendingCount}</span>
              </button>
            )}
            {archivedCount > 0 && (
              <button type="button" className="settings-modal-btn" onClick={() => onAdminView?.('archived')}>
                <Archive size={16} />
                View Archived Media
                <span className="settings-alias-current">{archivedCount}</span>
              </button>
            )}
            <button type="button" className="settings-modal-btn" onClick={runBackfillThumbnails} disabled={thumbBusy}>
              <ImageIcon size={16} />
              {thumbBusy ? 'Fetching thumbnails…' : 'Backfill Thumbnails'}
              {thumbResult && <span className="settings-alias-current">{thumbResult}</span>}
            </button>
            <button type="button" className="settings-modal-btn" onClick={runSweepDeadLinks} disabled={sweepBusy}>
              <Unlink size={16} />
              {sweepBusy ? 'Checking links…' : 'Sweep Dead Links'}
              {sweepResult && <span className="settings-alias-current">{sweepResult}</span>}
            </button>
            <button type="button" className="settings-modal-btn" onClick={() => setEditLogOpen(true)}>
              <History size={16} />
              Edit Log
            </button>
            <button type="button" className="settings-modal-btn" onClick={() => setPromosOpen(true)}>
              <Rocket size={16} />
              Pending Featured
            </button>
            <button type="button" className="settings-modal-btn" onClick={() => setUsersOpen(true)}>
              <Users size={16} />
              Manage Users
            </button>
            <button type="button" className="settings-modal-btn" onClick={() => setRulesOpen(true)}>
              <Scale size={16} />
              Sport Rules
            </button>
            <button type="button" className="settings-modal-btn" onClick={() => setGuideOpen(true)}>
              <MessageSquareQuote size={16} />
              How to Respond
            </button>
            </>)}
          </div>
        )}
        {isJanitor && (
          <div className="settings-admin-panel">
            <div className="settings-admin-panel-title">
              <Brush size={14} />
              <span>Janitor</span>
            </div>
            <button type="button" className="settings-modal-btn" onClick={() => setEditLogOpen(true)}>
              <History size={16} />
              Edit Log
            </button>
          </div>
        )}
        <button className="settings-modal-btn" onClick={() => { window.location.href = '/promo'; }}>
          <Globe size={16} />
          Go To Landing Page
        </button>
        {!isAdmin && (
          <button className="settings-modal-btn" onClick={() => {
            window.history.pushState({}, '', '/checkout');
            window.dispatchEvent(new PopStateEvent('popstate'));
            onClose();
          }}>
            <CreditCard size={16} />
            Manage Subscription
          </button>
        )}
        <button className="settings-modal-btn" onClick={() => window.open(DISCORD_INVITE, '_blank', 'noopener,noreferrer')}>
          <MessagesSquare size={16} />
          Join the Discord
        </button>
        <button className="settings-modal-btn settings-modal-btn--danger" onClick={() => { onLogOut(); onClose(); }}>
          <LogOut size={16} />
          Sign Out
        </button>
        <span className="settings-version">v{__APP_VERSION__}</span>
      </div>

      {/* Over the phone frame like Manage Users, not the whole viewport —
          a fixed backdrop escapes the frame on desktop. */}
      {giftOpen && createPortal(
        <div className="frame-modal-backdrop" onClick={() => setGiftOpen(false)}>
          <div className="gift-modal" onClick={(e) => e.stopPropagation()}>
            <button className="gift-modal-close" onClick={() => setGiftOpen(false)} aria-label="Close">
              <X size={18} />
            </button>
            {!giftCode ? (
              <form onSubmit={(e) => { e.preventDefault(); handleGenerateCode(); }}>
                <label className="gift-modal-label" htmlFor="gift-months">
                  Create a gift code that lasts how many months
                </label>
                <input
                  id="gift-months"
                  type="text"
                  inputMode="numeric"
                  className="gift-modal-input"
                  value={giftMonths}
                  onChange={(e) => setGiftMonths(e.target.value.replace(/\D/g, ''))}
                  autoFocus
                />
                {giftErr && <span className="gift-modal-error">{giftErr}</span>}
                <button className="gift-modal-submit" type="submit" disabled={giftBusy || !giftMonths}>
                  {giftBusy ? 'Generating…' : 'Generate Code'}
                </button>
              </form>
            ) : (
              <div className="gift-modal-result">
                <span className="gift-modal-success">Code created &amp; copied to clipboard</span>
                <code className="gift-modal-code">{giftCode}</code>
                <button className="gift-modal-submit" onClick={() => copyCode(giftCode)}>
                  {copied === giftCode ? 'Copied' : 'Copy again'}
                </button>
                <button className="gift-modal-link" onClick={() => setGiftCode(null)}>
                  Make another
                </button>
              </div>
            )}

            {/* Everything minted so far. Unused codes never expire, so the
                countdown column only fills in once one is redeemed. */}
            <div className="code-table-wrap">
              <div className="code-table-head">
                <span>Codes</span>
                {codes && <span className="code-table-count">{codes.length}</span>}
              </div>
              {codesErr ? (
                <span className="gift-modal-error">{codesErr}</span>
              ) : !codes ? (
                <span className="code-table-empty">Loading…</span>
              ) : codes.length === 0 ? (
                <span className="code-table-empty">None yet.</span>
              ) : (
                <table className="code-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Length</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th aria-label="Copy" />
                    </tr>
                  </thead>
                  <tbody>
                    {codes.map((c) => (
                      <tr key={c.code} className={c.used && !c.multiUse ? 'code-row--used' : undefined}>
                        <td><code>{c.code}</code></td>
                        <td>{Math.round((c.days * 12) / 365)} mo</td>
                        <td>
                          {c.multiUse
                            ? `${c.disabled ? 'Disabled' : 'Multi-use'} · ${c.uses ?? 0} used`
                            : c.used
                              ? `${c.usedBy ?? 'Redeemed'}${c.daysLeft != null ? ` · ${c.daysLeft}d left` : ''}`
                              : 'Unused'}
                        </td>
                        <td>{c.createdAt ? new Date(c.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}</td>
                        <td>
                          <button
                            className="code-copy-btn"
                            onClick={() => copyCode(c.code)}
                            aria-label={`Copy ${c.code}`}
                            title="Copy code"
                          >
                            {copied === c.code ? <Check size={14} /> : <Copy size={14} />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>,
        document.querySelector('.phone-frame') ?? document.body,
      )}
      {editLogOpen && <EditLog onClose={() => setEditLogOpen(false)} />}
      {usersOpen && isAdmin && <UserManager onClose={() => setUsersOpen(false)} />}
      {promosOpen && isAdmin && <PendingPromotions onClose={() => setPromosOpen(false)} />}
      {rulesOpen && isAdmin && <SportRulesModal onClose={() => setRulesOpen(false)} />}
      {guideOpen && isAdmin && <ResponseGuideModal onClose={() => setGuideOpen(false)} />}
    </div>
  );

  if (inline) return content;

  return (
    <div className="settings-modal-backdrop" onClick={onClose}>
      {content}
    </div>
  );
}
